import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Account } from '../../interfaces/accounting.interface';
import {
    FiscalYearClose,
    YearEndClosePreview,
    YearEndCloseSettings,
} from '../../interfaces/yearEndClose.interface';
import { AccountService } from '../../services/account.service';
import { YearEndCloseService } from '../../services/yearEndClose.service';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { formatDate } from '../../utils/dateUtils';
import ScrollableTable from '../common/ScrollableTable';
import AccountAutocomplete from '../common/AccountAutocomplete';

const money = (v: number | null | undefined) =>
    (v ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_COLOR: Record<string, 'default' | 'success' | 'warning' | 'info'> = {
    CLOSED: 'success',
    REOPENED: 'warning',
    OPEN: 'default',
    IN_PROGRESS: 'info',
};

const STATUS_LABEL: Record<string, string> = {
    CLOSED: 'Closed',
    REOPENED: 'Reopened',
    OPEN: 'Not closed',
    IN_PROGRESS: 'In progress',
};

const YearEndClose: React.FC = () => {
    const toast = useToast();
    const canManageAccounts = usePermission(PERMISSIONS.ACCOUNT_MANAGE);
    const canPost = usePermission(PERMISSIONS.JOURNAL_ENTRY_POST);
    // The server needs BOTH keys to close or reopen (it posts a journal and moves the ledger lock).
    const canClose = canManageAccounts && canPost;

    const [years, setYears] = useState<FiscalYearClose[]>([]);
    const [loading, setLoading] = useState(true);
    const [settings, setSettings] = useState<YearEndCloseSettings | null>(null);
    const [equityAccounts, setEquityAccounts] = useState<Account[]>([]);
    const [reAccountId, setReAccountId] = useState('');
    const [savingSettings, setSavingSettings] = useState(false);

    const [preview, setPreview] = useState<YearEndClosePreview | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [confirmClose, setConfirmClose] = useState(false);
    const [busy, setBusy] = useState(false);

    const [reopenTarget, setReopenTarget] = useState<FiscalYearClose | null>(null);
    const [reopenReason, setReopenReason] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [list, s] = await Promise.all([YearEndCloseService.list(), YearEndCloseService.getSettings()]);
            setYears(list);
            setSettings(s);
            setReAccountId(s.retainedEarningsAccountId ?? '');
        } catch (err) {
            toast.showApiError(err, 'Failed to load fiscal years');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        load();
        AccountService.getAccountsByType('EQUITY')
            .then((a: Account[]) => setEquityAccounts(a ?? []))
            .catch(() => setEquityAccounts([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const settingsDirty = useMemo(
        () => (settings?.retainedEarningsAccountId ?? '') !== reAccountId,
        [settings, reAccountId],
    );

    const saveSettings = async () => {
        setSavingSettings(true);
        try {
            const s = await YearEndCloseService.updateSettings(reAccountId || null);
            setSettings(s);
            setReAccountId(s.retainedEarningsAccountId ?? '');
            toast.showSuccess('Retained earnings account saved');
        } catch (err) {
            toast.showApiError(err, 'Failed to save the retained earnings account');
        } finally {
            setSavingSettings(false);
        }
    };

    const openPreview = async (year: FiscalYearClose) => {
        setPreview(null);
        setPreviewLoading(true);
        try {
            setPreview(await YearEndCloseService.preview(year.fiscalYearStart));
        } catch (err) {
            toast.showApiError(err, 'Failed to load the close preview');
        } finally {
            setPreviewLoading(false);
        }
    };

    const closeYear = async () => {
        if (!preview) return;
        setBusy(true);
        try {
            const res = await YearEndCloseService.close(preview.fiscalYearStart);
            toast.showSuccess(`Fiscal year ${formatDate(preview.fiscalYearStart)} to ${formatDate(preview.fiscalYearEnd)} closed`);
            res.warnings.filter(w => w.includes('draft')).forEach(w => toast.showWarning(w));
            setConfirmClose(false);
            setPreview(null);
            await load();
        } catch (err) {
            toast.showApiError(err, 'Failed to close the fiscal year');
        } finally {
            setBusy(false);
        }
    };

    const reopenYear = async () => {
        if (!reopenTarget?.closeId) return;
        setBusy(true);
        try {
            const res = await YearEndCloseService.reopen(reopenTarget.closeId, reopenReason.trim());
            toast.showSuccess('Fiscal year reopened; the closing entry was reversed');
            res.warnings.forEach(w => toast.showWarning(w));
            setReopenTarget(null);
            setReopenReason('');
            await load();
        } catch (err) {
            toast.showApiError(err, 'Failed to reopen the fiscal year');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box data-help-page="accounting.yearEnd.page" sx={{ p: 2.5 }}>
            <Typography variant="h5" sx={{ mb: 0.5 }}>Year-End Close</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Closing a fiscal year posts one journal dated its last day that moves every revenue and expense balance
                (per segment) into retained earnings, then locks the books through that date. The income statement still
                shows the closed year's profit; the balance sheet does not change.
            </Typography>

            <Paper data-help-section="accounting.yearEnd.settings" variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Retained earnings account</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                    <Box sx={{ flex: '1 1 320px', maxWidth: 520 }}>
                        <AccountAutocomplete data-help="accounting.yearEnd.account"
                            accounts={equityAccounts}
                            value={reAccountId}
                            onChange={setReAccountId}
                            label="Equity account receiving each year's net profit"
                            disabled={!canManageAccounts}
                            size="small"
                            fullWidth
                        />
                    </Box>
                    {settings?.suggestedAccountId && settings.suggestedAccountId !== reAccountId && canManageAccounts && (
                        <Button data-help="accounting.yearEnd.useSuggested" size="small" onClick={() => setReAccountId(settings.suggestedAccountId ?? '')}>
                            Use {settings.suggestedAccountLabel}
                        </Button>
                    )}
                    <Button data-help="accounting.yearEnd.save"
                        variant="contained"
                        onClick={saveSettings}
                        disabled={!canManageAccounts || !settingsDirty || savingSettings}
                    >
                        Save
                    </Button>
                </Box>
            </Paper>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : (
                <ScrollableTable>
                    <Table data-help-section="accounting.yearEnd.table" size="small" sx={{ minWidth: 820 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="accounting.yearEnd.col.year">Fiscal year</TableCell>
                                <TableCell data-help="accounting.yearEnd.col.status">Status</TableCell>
                                <TableCell data-help="accounting.yearEnd.col.profit" align="right">Net profit (year)</TableCell>
                                <TableCell data-help="accounting.yearEnd.col.swept" align="right">Swept into retained earnings</TableCell>
                                <TableCell data-help="accounting.yearEnd.col.journal">Closing journal</TableCell>
                                <TableCell data-help="accounting.yearEnd.col.actions" align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {years.length === 0 && (
                                <TableRow><TableCell colSpan={6} align="center">No fiscal years yet.</TableCell></TableRow>
                            )}
                            {years.map(y => (
                                <TableRow key={y.fiscalYearStart} data-testid={`fy-${y.fiscalYearStart}`}>
                                    <TableCell>{formatDate(y.fiscalYearStart)} to {formatDate(y.fiscalYearEnd)}</TableCell>
                                    <TableCell>
                                        <Chip size="small" label={STATUS_LABEL[y.status] ?? y.status} color={STATUS_COLOR[y.status] ?? 'default'} />
                                    </TableCell>
                                    <TableCell align="right">{money(y.netProfit)}</TableCell>
                                    <TableCell align="right">{y.netProfitClosed == null ? '-' : money(y.netProfitClosed)}</TableCell>
                                    <TableCell>
                                        {y.closingEntryId && (
                                            <RouterLink data-help="accounting.yearEnd.action.viewJournal" to={`/accounting/journal-entries/${y.closingEntryId}`}>View journal</RouterLink>
                                        )}
                                        {y.status === 'REOPENED' && y.reversalEntryId && (
                                            <>{' '}<RouterLink data-help="accounting.yearEnd.action.viewReversal" to={`/accounting/journal-entries/${y.reversalEntryId}`}>(reversal)</RouterLink></>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {y.canClose && (
                                            <Button data-help="accounting.yearEnd.action.preview" size="small" variant="outlined" onClick={() => openPreview(y)} disabled={!canClose}>
                                                Preview and close
                                            </Button>
                                        )}
                                        {y.status === 'CLOSED' && (
                                            <Button data-help="accounting.yearEnd.action.reopen"
                                                size="small"
                                                color="warning"
                                                onClick={() => setReopenTarget(y)}
                                                disabled={!canClose}
                                            >
                                                Reopen
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </ScrollableTable>
            )}
            {!canClose && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Closing or reopening a year needs both the manage-accounts and post-journal permissions.
                </Typography>
            )}

            {/* Preview */}
            <Dialog data-help-page="accounting.yearEnd.preview.page" open={previewLoading || !!preview} onClose={() => setPreview(null)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {preview ? `Close ${formatDate(preview.fiscalYearStart)} to ${formatDate(preview.fiscalYearEnd)}` : 'Loading preview'}
                </DialogTitle>
                <DialogContent dividers>
                    {previewLoading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>}
                    {preview && (
                        <>
                            {preview.blockers.map(b => <Alert key={b} severity="error" sx={{ mb: 1 }}>{b}</Alert>)}
                            {preview.warnings.map(w => <Alert key={w} severity="info" sx={{ mb: 1 }}>{w}</Alert>)}
                            {preview.includedEarlierYears.length > 0 && (
                                <Alert severity="warning" sx={{ mb: 1 }}>
                                    This close also sweeps earlier years that were never closed:{' '}
                                    {preview.includedEarlierYears.map(y =>
                                        `${formatDate(y.fiscalYearStart)} to ${formatDate(y.fiscalYearEnd)} (profit ${money(y.netProfit)})`).join('; ')}
                                </Alert>
                            )}
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Retained earnings account: <b>{preview.retainedEarningsAccountLabel ?? 'not chosen'}</b>
                            </Typography>
                            <ScrollableTable>
                                <Table data-help-section="accounting.yearEnd.previewTable" size="small" sx={{ minWidth: 640 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell data-help="accounting.yearEnd.preview.col.account">Account</TableCell>
                                            <TableCell data-help="accounting.yearEnd.preview.col.segment">Segment</TableCell>
                                            <TableCell data-help="accounting.yearEnd.preview.col.type">Type</TableCell>
                                            <TableCell data-help="accounting.yearEnd.preview.col.balance" align="right">Balance to close</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {preview.lines.map((l, i) => (
                                            <TableRow key={`${l.accountId}-${l.segmentId}-${i}`}>
                                                <TableCell>{l.accountCode} - {l.accountName}</TableCell>
                                                <TableCell>{l.segmentName}</TableCell>
                                                <TableCell>{l.accountType === 'REVENUE' ? 'Revenue' : 'Expense'}</TableCell>
                                                <TableCell align="right">{money(l.balance)}</TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow>
                                            <TableCell colSpan={3}><b>Total revenue</b></TableCell>
                                            <TableCell align="right">{money(preview.totalRevenue)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell colSpan={3}><b>Total expense</b></TableCell>
                                            <TableCell align="right">{money(preview.totalExpense)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell colSpan={3}><b>Net profit to retained earnings</b></TableCell>
                                            <TableCell align="right"><b>{money(preview.netProfit)}</b></TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </ScrollableTable>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button data-help="accounting.yearEnd.preview.cancel" onClick={() => setPreview(null)}>Cancel</Button>
                    <Button data-help="accounting.yearEnd.action.close"
                        variant="contained"
                        disabled={!preview || !preview.canClose || !canClose}
                        onClick={() => setConfirmClose(true)}
                    >
                        Close fiscal year
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Close confirmation */}
            <Dialog data-help-page="accounting.yearEnd.confirm.page" open={confirmClose && !!preview} onClose={() => !busy && setConfirmClose(false)}>
                <DialogTitle>Close this fiscal year?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 1 }}>
                        A closing journal dated {preview ? formatDate(preview.fiscalYearEnd) : ''} will be posted and the books
                        will be locked through that date: nothing can be posted, edited or reversed on or before it until the
                        year is reopened.
                    </Typography>
                    <Typography>Reopening reverses the closing journal and restores the previous lock date.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button data-help="accounting.yearEnd.confirm.back" onClick={() => setConfirmClose(false)} disabled={busy}>Back</Button>
                    <Button data-help="accounting.yearEnd.action.confirmClose" variant="contained" color="primary" onClick={closeYear} disabled={busy}>
                        {busy ? 'Closing...' : 'Close and lock'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reopen */}
            <Dialog data-help-page="accounting.yearEnd.reopen.page" open={!!reopenTarget} onClose={() => !busy && setReopenTarget(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Reopen this fiscal year?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        The closing journal for {reopenTarget ? `${formatDate(reopenTarget.fiscalYearStart)} to ${formatDate(reopenTarget.fiscalYearEnd)}` : ''} is
                        reversed (dated the last day of the year) and the lock date goes back to what it was before the close,
                        so entries in this year can be posted again. Close the year again when you are done.
                    </Typography>
                    <TextField data-help="accounting.yearEnd.reopen.reason"
                        label="Reason (optional)"
                        value={reopenReason}
                        onChange={e => setReopenReason(e.target.value)}
                        inputProps={{ maxLength: 500 }}
                        size="small"
                        fullWidth
                    />
                </DialogContent>
                <DialogActions>
                    <Button data-help="accounting.yearEnd.reopen.cancel" onClick={() => setReopenTarget(null)} disabled={busy}>Cancel</Button>
                    <Button data-help="accounting.yearEnd.action.confirmReopen" variant="contained" color="warning" onClick={reopenYear} disabled={busy}>
                        {busy ? 'Reopening...' : 'Reopen year'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default YearEndClose;
